import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import MarketingSiteLayout from '../../marketing/MarketingSiteLayout'
import PublicLandingInquiryForm from '../../marketing/PublicLandingInquiryForm'
import { LANDING_PATH } from '../../marketing/marketingNav'

const INQUIRY_SECTION_ID = 'b2b-inquiry'

const organizationOnlyTools = [
  '조직 홈페이지와 배너',
  '구성원과 권한 관리',
  '이용 현황 확인',
  '인원에 맞춘 계약',
]

const organizationFitQuestions = [
  '구성원이 늘어나 전체 이용 현황을 확인하기 어려워요.',
  '조직 구조에 맞춰 구성원과 권한을 나누고 싶어요.',
  '우리 조직 이름으로 소개와 채용 소식을 알리고 싶어요.',
  '사용할 인원에 맞는 비용을 미리 알고 싶어요.',
]

const organizationFeatureShowcases = [
  {
    number: '01',
    eyebrow: '조직 홈페이지와 배너',
    title: '우리 조직의 이름으로 알리고, 문의를 받습니다.',
    description: '조직 홈페이지에 소개와 채용 소식을 담고, PC·모바일·앱 배너로 공지와 캠페인을 알릴 수 있습니다. 홈페이지에서는 입사 문의도 받을 수 있습니다.',
    chips: ['조직 소개', '채용 소식', '입사 문의'],
    Screen: OrganizationChannelScreen,
  },
  {
    number: '02',
    eyebrow: '관리자 화면',
    title: '구성원과 이용 현황을 한눈에 살펴봅니다.',
    description: '구성원 활동, 자주 쓰는 기능, 계약 인원과 남은 인원을 한곳에서 확인할 수 있습니다.',
    chips: ['구성원 활동', '이용 현황', '계약 인원'],
    Screen: OrganizationDashboardScreen,
  },
  {
    number: '03',
    eyebrow: '구성원과 권한 관리',
    title: '조직 구조에 맞게 나누고, 필요한 권한만 줍니다.',
    description: '상위·하위 조직을 만들고 구성원의 역할과 소속을 정할 수 있습니다. 관리 권한도 역할에 맞게 나눌 수 있습니다.',
    chips: ['상위·하위 조직', '역할과 소속', '관리 권한'],
    Screen: OrganizationStructureScreen,
  },
  {
    number: '04',
    eyebrow: '사용 인원에 맞춘 단체계약',
    title: '몇 명이 어떻게 쓸지에 맞춰 계약합니다.',
    description: '사용할 인원과 필요한 기능을 먼저 확인한 뒤 비용과 할인 조건을 안내해 드립니다.',
    chips: ['사용 인원', '필요한 기능', '비용·할인 안내'],
    Screen: OrganizationContractScreen,
  },
]

const adoptionSteps = [
  {
    number: '01',
    title: '현재 상황을 알려주세요',
    description: '사용할 인원과 지금 가장 필요한 부분을 간단히 남겨주세요.',
  },
  {
    number: '02',
    title: '알맞은 방법을 함께 정해요',
    description: '필요한 기능과 관리 방법, 예상 비용을 함께 확인합니다.',
  },
  {
    number: '03',
    title: '준비가 끝나면 시작해요',
    description: '조직 설정과 구성원 등록을 마친 뒤 설계EZ를 함께 사용합니다.',
  },
]

function SectionEyebrow({ children, tone = 'sky' }) {
  const textClassName = tone === 'light' ? 'text-sky-100' : 'text-brand-sky'

  return <p className={'text-sm font-bold tracking-wide ' + textClassName}>{children}</p>
}

function OrganizationMonitorFrame({ children }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_32px_90px_-62px_rgba(15,23,42,0.72)]">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        <span className="ml-2 text-xs font-bold text-slate-500">설계EZ 조직 관리</span>
      </div>
      <div className="min-h-[278px] bg-slate-50 p-4 sm:p-5">{children}</div>
    </div>
  )
}

function OrganizationChannelScreen() {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <div className="bg-slate-950 px-5 py-4 text-white">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold tracking-wide">우리 조직</span>
          <span className="text-xs font-bold text-sky-200">조직 홈페이지</span>
        </div>
        <p className="mt-7 text-xl font-black leading-snug">우리 조직을 소개하고<br />새 설계사를 만나는 공간</p>
        <div className="mt-5 inline-flex rounded-md bg-white px-3 py-2 text-xs font-bold text-slate-950">입사 문의 받기</div>
      </div>
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <div className="rounded-md bg-sky-50 p-3">
          <p className="text-xs font-bold text-brand-navy">조직 소개</p>
          <p className="mt-2 text-xs leading-5 text-slate-600">조직 이름과 채용 소식을 한곳에 담습니다.</p>
        </div>
        <div className="rounded-md bg-slate-100 p-3">
          <p className="text-xs font-bold text-slate-800">조직 배너</p>
          <p className="mt-2 text-xs leading-5 text-slate-600">PC·모바일·앱에서 공지와 캠페인을 알립니다.</p>
        </div>
      </div>
    </div>
  )
}

function OrganizationDashboardScreen() {
  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-brand-navy">관리자 화면</p>
          <p className="mt-1 text-sm font-bold text-slate-950">구성원과 이용 현황</p>
        </div>
        <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[10px] font-bold text-brand-navy">관리자용</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {['구성원 활동', '계약 인원', '주요 기능'].map((item) => (
          <div key={item} className="rounded-md border border-slate-200 bg-white p-3">
            <p className="text-[11px] font-bold text-slate-500">{item}</p>
            <div className="mt-4 h-2 rounded-full bg-slate-100">
              <div className="h-2 rounded-full bg-brand-sky" style={{ width: item === '계약 인원' ? '66%' : '78%' }} />
            </div>
            <p className="mt-3 text-xs font-bold text-slate-800">현황 보기</p>
          </div>
        ))}
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold text-slate-800">구성원별 이용 현황</span>
          <span className="font-medium text-slate-500">자세히 보기</span>
        </div>
        <div className="mt-3 grid gap-2">
          {['구성원 활동 확인', '계약 인원 확인', '주요 기능 이용'].map((item) => (
            <div key={item} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs">
              <span className="font-medium text-slate-600">{item}</span>
              <span className="h-2 w-12 rounded-full bg-sky-100" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function OrganizationStructureScreen() {
  return (
    <div className="grid gap-4">
      <div className="rounded-md border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-brand-navy">구성원과 권한</p>
            <p className="mt-1 text-sm font-bold text-slate-950">조직별 구성원</p>
          </div>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-600">역할에 맞게</span>
        </div>
      </div>
      <div className="rounded-md border border-dashed border-sky-200 bg-sky-50 p-4">
        <div className="mx-auto w-fit rounded-md bg-brand-navy px-4 py-2 text-xs font-bold text-white">상위 조직</div>
        <div className="mx-auto h-5 w-px bg-sky-200" />
        <div className="grid gap-3 sm:grid-cols-2">
          {['하위 조직', '구성원'].map((item) => (
            <div key={item} className="rounded-md border border-sky-100 bg-white p-3">
              <p className="text-xs font-bold text-slate-800">{item}</p>
              <p className="mt-2 text-[11px] font-medium text-slate-500">{item === '하위 조직' ? '조직별로 나눠 관리' : '역할과 소속 관리'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function OrganizationContractScreen() {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold text-brand-navy">단체계약</p>
          <p className="mt-2 text-lg font-black text-slate-950">사용 인원에 맞춘 계약</p>
        </div>
        <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[10px] font-bold text-brand-navy">상담 후 안내</span>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {['사용 인원', '조직 구조', '필요한 기능'].map((item) => (
          <div key={item} className="rounded-md bg-slate-50 p-3">
            <p className="text-xs font-bold text-slate-800">{item}</p>
            <p className="mt-2 text-[11px] leading-5 text-slate-500">우리 조직에 맞게 확인</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-md bg-slate-950 px-4 py-3 text-xs font-bold text-white">
        <span>이용 방법 · 비용 함께 확인</span>
        <ArrowRight className="h-4 w-4 text-sky-200" aria-hidden="true" />
      </div>
    </div>
  )
}

export default function B2bPartnershipPage() {
  const location = useLocation()

  const scrollToInquiry = React.useCallback(() => {
    document.getElementById(INQUIRY_SECTION_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [])

  React.useEffect(() => {
    if (location.hash === '#inquiry') scrollToInquiry()
  }, [location.hash, scrollToInquiry])

  return (
    <MarketingSiteLayout>
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          aria-hidden="true"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(186, 230, 253, 0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(186, 230, 253, 0.12) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-brand-sky/30 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-4 py-16 md:py-24 lg:py-28">
          <nav aria-label="현재 위치" className="text-sm text-slate-300">
            <Link to={LANDING_PATH} className="transition hover:text-white">
              설계EZ 소개
            </Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-white">B2B 제휴</span>
          </nav>

          <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-end lg:gap-16">
            <div className="max-w-3xl">
              <SectionEyebrow tone="light">설계EZ 조직 도입</SectionEyebrow>
              <h1 className="mt-5 text-4xl font-black leading-[1.14] tracking-tight sm:text-5xl lg:text-6xl">
                설계사는 그대로 쓰고,
                <br className="hidden sm:block" /> 관리자는 한눈에 봅니다.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-slate-200 sm:text-lg">
                조직 홈페이지와 배너, 구성원 관리, 이용 현황 확인, 인원에 맞춘 단체계약까지. 설계EZ를 우리 조직에 맞게 함께 시작할 수 있습니다.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a
                  href="#inquiry"
                  onClick={scrollToInquiry}
                  className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-md bg-white px-6 text-sm font-bold text-brand-navy shadow-lg shadow-black/20 transition hover:bg-sky-50"
                >
                  우리 조직 도입 상담받기
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
                <Link
                  to="/pricing"
                  className="inline-flex min-h-[50px] items-center justify-center rounded-md border border-white/35 bg-white/10 px-6 text-sm font-bold text-white transition hover:bg-white/20"
                >
                  이용요금 보기
                </Link>
              </div>
            </div>

            <aside className="rounded-lg border border-white/15 bg-white/10 p-5 shadow-2xl shadow-black/20 backdrop-blur-sm">
              <p className="text-sm font-bold text-sky-100">조직에서 함께 쓸 때</p>
              <p className="mt-2 text-lg font-black text-white">이 네 가지가 더해집니다.</p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                {organizationOnlyTools.map((tool) => (
                  <div key={tool} className="rounded-md bg-white/10 px-3 py-3 text-xs font-bold leading-5 text-slate-100">
                    <CheckCircle2 className="mb-2 h-4 w-4 text-sky-200" aria-hidden="true" />
                    {tool}
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24" aria-labelledby="b2b-fit-title">
        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-3xl">
            <SectionEyebrow>이런 조직에 잘 맞아요</SectionEyebrow>
            <h2 id="b2b-fit-title" className="mt-3 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
              이런 고민이 있다면 조직 도입을 살펴보세요.
            </h2>
            <p className="mt-5 text-base leading-7 text-slate-600 sm:text-lg">
              보험대리점(GA)처럼 여러 설계사가 함께 쓰는 조직에서 자주 나오는 고민입니다.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {organizationFitQuestions.map((question, index) => (
              <article key={question} className="flex gap-4 rounded-lg border border-slate-200 bg-slate-50 p-6">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-navy text-xs font-black text-white">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="pt-1 text-base font-bold leading-7 text-slate-800">{question}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28" aria-labelledby="b2b-feature-showcase-title">
        <div className="mx-auto max-w-7xl px-4">
          <div className="mx-auto max-w-3xl text-center">
            <SectionEyebrow>조직으로 이용하면 달라지는 점</SectionEyebrow>
            <h2 id="b2b-feature-showcase-title" className="mt-3 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
              우리 조직을 알리고, 함께 쓰는 현황을 살펴보세요.
            </h2>
            <p className="mt-5 text-base leading-7 text-slate-600 sm:text-lg">
              조직 홈페이지부터 구성원 관리, 이용 현황, 단체계약까지 조직에 필요한 기능을 한곳에서 제공합니다.
            </p>
          </div>

          <div className="mt-16 grid gap-20">
            {organizationFeatureShowcases.map(({ number, eyebrow, title, description, chips, Screen }, index) => (
              <article key={title} className="grid gap-12 lg:grid-cols-[0.8fr_1fr] lg:items-center lg:gap-16 xl:gap-20">
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <p className="text-sm font-bold text-brand-sky">{number} · {eyebrow}</p>
                  <h3 className="mt-4 text-3xl font-black leading-tight text-slate-950">{title}</h3>
                  <p className="mt-4 max-w-lg text-base font-medium leading-7 text-slate-600">{description}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {chips.map((chip) => (
                      <span key={chip} className="rounded-full bg-[#eef5ff] px-3 py-1.5 text-xs font-bold text-brand-navy">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
                <OrganizationMonitorFrame>
                  <Screen />
                </OrganizationMonitorFrame>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24" aria-labelledby="b2b-adoption-title">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16">
            <div>
              <SectionEyebrow>도입 방법</SectionEyebrow>
              <h2 id="b2b-adoption-title" className="mt-3 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
                처음부터 다 정해 오실 필요는 없습니다.
              </h2>
              <p className="mt-5 max-w-lg text-base leading-7 text-slate-600">
                사용 인원과 궁금한 점만 알려주시면 우리 조직에 맞는 이용 방법과 비용을 안내해 드립니다.
              </p>
              <a
                href="#inquiry"
                onClick={scrollToInquiry}
                className="mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-md bg-brand-navy px-6 text-sm font-bold text-white transition hover:bg-sky-700"
              >
                도입 상담으로 이동
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>

            <ol className="grid gap-4">
              {adoptionSteps.map(({ number, title, description }) => (
                <li key={number} className="grid gap-4 rounded-lg border border-slate-200 bg-slate-50 p-6 sm:grid-cols-[3.5rem_1fr] sm:items-start">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-sm font-black text-brand-navy">
                    {number}
                  </span>
                  <div>
                    <h3 className="text-lg font-bold text-slate-950">{title}</h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">{description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section id={INQUIRY_SECTION_ID} className="scroll-mt-24 bg-white py-16 md:py-24" aria-labelledby="b2b-inquiry-title" tabIndex={-1}>
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid gap-10 rounded-lg bg-slate-950 p-6 text-white shadow-[0_30px_90px_-55px_rgba(15,23,42,0.72)] md:p-10 lg:grid-cols-[0.78fr_1.22fr] lg:gap-14">
            <div className="lg:py-3">
              <SectionEyebrow tone="light">조직 도입 상담</SectionEyebrow>
              <h2 id="b2b-inquiry-title" className="mt-4 text-3xl font-black leading-tight sm:text-4xl">
                우리 조직에 맞는 이용 방법을
                <br /> 알아보세요.
              </h2>
              <p className="mt-5 max-w-md text-sm leading-7 text-slate-300 sm:text-base">
                아직 구체적으로 정하지 않았어도 괜찮습니다. 사용할 인원과 궁금한 점만 남겨주세요.
              </p>
              <ul className="mt-8 grid gap-3 text-sm font-medium text-slate-200">
                {['예상 사용 인원', '필요한 조직 기능', '궁금한 점 또는 원하는 시작 시기'].map((item) => (
                  <li key={item} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-200" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-white p-6 text-slate-900 shadow-xl shadow-black/20 sm:p-8">
              <PublicLandingInquiryForm organizationMode scrollIntoViewOnSuccessId={INQUIRY_SECTION_ID} />
            </div>
          </div>
        </div>
      </section>
    </MarketingSiteLayout>
  )
}
